import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Delete, LoaderCircle } from 'lucide-react';
import { SUPPORT_PHONE_DISPLAY } from './SupportContact';
import './Login.css';

/** Τι έγινε με μια αποστολή: 'rejected' = απορρίφθηκε (οι κουκκίδες αδειάζουν), 'rate_limited' = 429. */
export type PinSubmitResult = 'ok' | 'rejected' | 'rate_limited';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  role: string;
  role_display: string;
  pin_length: number;
  has_pin: boolean;
}

interface PinEntryProps {
  employee: Employee;
  /**
   * Καλείται ΜΙΑ φορά για κάθε συμπληρωμένο PIN (όχι σε κάθε re-render, ούτε ξανά με Enter/διπλό πάτημα).
   * Αν γυρίσει Promise, τα πλήκτρα μένουν κλειδωμένα ώσπου να απαντήσει· 'rejected' αδειάζει τις κουκκίδες.
   */
  onSubmit: (pin: string) => void | Promise<PinSubmitResult | void>;
  onCancel: () => void;
  error: string | null;
  loading: boolean;
  /**
   * 429 (όριο προσπαθειών): ως πότε (epoch ms) ο server δεν δέχεται άλλη προσπάθεια. Όσο ισχύει: αντίστροφη
   * μέτρηση, κλειδωμένα πλήκτρα, το PIN ΔΕΝ σβήνεται σαν λάθος· αδειάζει όταν τελειώσει η αναμονή, ώστε η
   * επόμενη προσπάθεια να είναι νέο πληκτρολόγημα (ποτέ αυτόματη αποστολή σε ταμείο που ίσως έμεινε μόνο του).
   */
  retryAt?: number | null;
  /** Το κατάστημα της συνεδρίας, για να βλέπει ο υπάλληλος πού συνδέεται (δεν αλλάζει τίποτα στη ροή). */
  storeName?: string;
}

/** Ως 90″ η αναμονή λέγεται σε δευτερόλεπτα, μετά σε λεπτά (το όριο του backend είναι παράθυρο 15′). */
const SECONDS_SHOWN_UP_TO = 90;

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * PIN υπαλλήλου (4 ψηφία, 6 ο διαχειριστής — pin_length). Στέλνει μόνο του μόλις συμπληρωθεί·
 * δέχεται και φυσικό πληκτρολόγιο (ψηφία, Backspace, Enter, Esc = πίσω).
 *
 * Νέα εμφάνιση (14/10/2026): μεγάλα πλήκτρα αφής (64px, 76px σε tablet, ποτέ κάτω από 56px), κουκκίδες
 * στο χρώμα brand, το σφάλμα ως κόκκινο κείμενο κάτω από τις κουκκίδες, και όλη η οθόνη χωρά σε
 * 1366×768 / 1024×768 χωρίς κύλιση. Η λογική (αποστολή, καθάρισμα στο σφάλμα, κλείδωμα όσο επαληθεύει,
 * πλήκτρα) έμεινε ίδια.
 *
 * Μία αποστολή ανά PIN (24/10/2026): πριν, η αποστολή γινόταν από useEffect πάνω στο [pin, loading, onSubmit]·
 * όταν ερχόταν «λάθος PIN», το ίδιο render έφερνε loading=false ΜΕ τις κουκκίδες ακόμη γεμάτες (το καθάρισμα
 * τρέχει στο αμέσως επόμενο effect) → δεύτερο POST με το ίδιο PIN. Τώρα στέλνει το πάτημα που συμπληρώνει το
 * PIN, μία φορά ανά πληκτρολόγημα (sentRef), και τίποτα δεν περνά όσο εκκρεμεί η απάντηση (busyRef — πιάνει
 * και τα γρήγορα διπλά πατήματα πριν προλάβει να έρθει το loading=true).
 */
const PinEntry: React.FC<PinEntryProps> = ({
  employee,
  onSubmit,
  onCancel,
  error,
  loading,
  retryAt = null,
  storeName
}) => {
  const { t } = useTranslation('auth');
  const [pin, setPinState] = useState('');
  const pinLength = employee.pin_length || 4;
  // Η τρέχουσα τιμή χωρίς να περιμένουμε render: δύο πατήματα στο ίδιο frame δεν ξεπερνούν το μήκος.
  const pinRef = useRef('');
  // Το τωρινό πλήρες PIN στάλθηκε ήδη· ξαναστέλνεται μόνο αν αλλάξει (σβήσιμο/νέα ψηφία).
  const sentRef = useRef(false);
  // Μια αποστολή περιμένει απάντηση (από τη στιγμή του πατήματος, πριν φτάσει το loading).
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const setPin = useCallback((next: string) => {
    pinRef.current = next;
    if (next.length < pinLength) sentRef.current = false;
    setPinState(next);
  }, [pinLength]);

  // 429: αντίστροφη μέτρηση ως το retryAt (ένα render ανά δευτερόλεπτο, μόνο όσο κρατά).
  const [tick, setTick] = useState(0);
  const waitMs = retryAt ? Math.max(0, retryAt - Date.now()) : 0;
  const rateLimited = waitMs > 0;
  useEffect(() => {
    if (!retryAt) return undefined;
    const left = retryAt - Date.now();
    if (left <= 0) return undefined;
    const timer = window.setTimeout(() => setTick(n => n + 1), left % 1000 || 1000);
    return () => window.clearTimeout(timer);
  }, [retryAt, tick]);

  // Τέλος αναμονής: οι κουκκίδες αδειάζουν — η νέα προσπάθεια είναι νέο πληκτρολόγημα.
  const wasRateLimitedRef = useRef(false);
  useEffect(() => {
    if (wasRateLimitedRef.current && !rateLimited) setPin('');
    wasRateLimitedRef.current = rateLimited;
  }, [rateLimited, setPin]);

  // Clear PIN on error
  useEffect(() => {
    if (error) {
      setPin('');
    }
  }, [error, setPin]);

  const blocked = loading || rateLimited;

  const submit = useCallback((value: string) => {
    if (sentRef.current || busyRef.current || blocked) return;
    sentRef.current = true;
    const result = onSubmit(value);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      busyRef.current = true;
      (result as Promise<PinSubmitResult | void>).then(
        outcome => {
          busyRef.current = false;
          if (mountedRef.current && outcome === 'rejected') setPin('');
        },
        () => {
          busyRef.current = false;
          if (mountedRef.current) setPin('');
        }
      );
    }
  }, [blocked, onSubmit, setPin]);

  const handleKeyPress = useCallback((key: string) => {
    if (blocked || busyRef.current) return;
    const current = pinRef.current;

    if (key === 'backspace') {
      setPin(current.slice(0, -1));
    } else if (key === 'clear') {
      setPin('');
    } else if (current.length < pinLength) {
      const next = current + key;
      setPin(next);
      // Το πάτημα που συμπληρώνει το PIN το στέλνει — μία φορά.
      if (next.length === pinLength) submit(next);
    }
  }, [blocked, pinLength, setPin, submit]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        handleKeyPress('backspace');
      } else if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && pinRef.current.length === pinLength) {
        // Μόνο αν το πλήρες PIN δεν έχει ήδη σταλεί (το submit το φυλάει).
        submit(pinRef.current);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress, onCancel, submit, pinLength]);

  const waitSeconds = Math.ceil(waitMs / 1000);
  const waitMessage = waitSeconds > SECONDS_SHOWN_UP_TO
    ? t('pin.tooManyAttemptsMinutes', { count: Math.ceil(waitSeconds / 60) })
    : t('pin.tooManyAttemptsSeconds', { count: waitSeconds });

  // Τα πλήκτρα είναι απλά <button> (όχι component ορισμένο μέσα στο render): έτσι δεν ξαναστήνονται σε
  // κάθε πάτημα και η εστίαση του πληκτρολογίου μένει πάνω τους.
  const digitKey = (digit: string) => (
    <button key={digit} type="button" className="xl-key" onClick={() => handleKeyPress(digit)} disabled={blocked}>
      {digit}
    </button>
  );

  return (
    <div className="xl-page">
      <header className="xl-bar">
        <button type="button" onClick={onCancel} className="xl-ghost">
          <ArrowLeft aria-hidden="true" />
          <span>{t('common:back')}</span>
        </button>
        {storeName && <span className="xl-bar-store" title={storeName}>{storeName}</span>}
      </header>

      <main className="xl-main xl-pin-main">
        <section className="xl-pin" aria-labelledby="xl-pin-name">
          <div className="xl-avatar">
            {employee.avatar_url ? (
              <img src={employee.avatar_url} alt={employee.first_name} />
            ) : (
              <span aria-hidden="true">{employee.first_name.charAt(0)}{employee.last_name.charAt(0)}</span>
            )}
          </div>
          <h1 id="xl-pin-name" className="xl-pin-name">
            {employee.first_name} {employee.last_name}
          </h1>
          <p className="xl-pin-role">{employee.role_display}</p>

          <p className="xl-pin-prompt">{t('enterPinPrompt', { length: pinLength })}</p>
          <div className="xl-dots" aria-hidden="true">
            {Array.from({ length: pinLength }).map((_, i) => (
              <span key={i} className="xl-dot" data-filled={i < pin.length ? 'true' : 'false'} data-testid="pin-dot" />
            ))}
          </div>

          <div className="xl-pin-status">
            {/* 429: όχι «Λάθος PIN» — πόση ώρα μένει (role=status: η μέτρηση δεν ξαναδιαβάζεται κάθε δευτερόλεπτο ως συναγερμός). */}
            {rateLimited
              ? <p className="xl-pin-error" role="status" data-testid="pin-rate-limit">{waitMessage}</p>
              : error && <p className="xl-pin-error" role="alert">{error}</p>}
            {loading && (
              <p className="xl-pin-loading">
                <LoaderCircle className="xl-spin" aria-hidden="true" />
                <span>{t('verifying')}</span>
              </p>
            )}
          </div>

          <div className="xl-pad">
            {DIGITS.map(digitKey)}
            <button
              type="button"
              className="xl-key xl-key-quiet"
              onClick={() => handleKeyPress('clear')}
              disabled={blocked || pin.length === 0}
            >
              {t('clearPin')}
            </button>
            {digitKey('0')}
            <button
              type="button"
              className="xl-key xl-key-quiet"
              onClick={() => handleKeyPress('backspace')}
              disabled={blocked || pin.length === 0}
              aria-label={t('pin.deleteDigit')}
            >
              <Delete aria-hidden="true" />
            </button>
          </div>
        </section>
      </main>

      {/* Τηλέφωνο υποστήριξης, ορατό όταν δεν μπορούν να συνδεθούν — στατικό κείμενο όπως πάντα στις
          οθόνες υπαλλήλου/PIN (SupportContact), όχι σύνδεσμος. */}
      <footer className="xl-foot">
        <span className="xl-support xl-foot-end">{t('pin.support')}<strong>{SUPPORT_PHONE_DISPLAY}</strong></span>
      </footer>
    </div>
  );
};

export default PinEntry;
